import { useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, UserPlus } from "lucide-react";
import { AddMemberError, useAddTeamMemberByEmail } from "@/hooks/useTeams";
import type { TeamMemberRole } from "@/types";

const ROLE_OPTIONS: { value: TeamMemberRole; labelKey: string }[] = [
  { value: "driver", labelKey: "teams.roleDriver" },
  { value: "engineer", labelKey: "teams.roleEngineer" },
  { value: "mechanic", labelKey: "teams.roleMechanic" },
  { value: "coach", labelKey: "teams.roleCoach" },
  { value: "director", labelKey: "teams.roleDirector" },
  { value: "other", labelKey: "teams.roleOther" },
];

const ERROR_KEYS: Record<string, string> = {
  userNotFound: "teams.addMemberUserNotFound",
  alreadyMember: "teams.addMemberAlreadyMember",
  unknown: "teams.addMemberFailed",
};

export default function AddMemberForm({ teamId }: { teamId: string }) {
  const { t } = useTranslation("pages");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamMemberRole>("driver");
  const addMember = useAddTeamMemberByEmail(teamId);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    addMember.mutate(
      { email: email.trim(), role },
      { onSuccess: () => setEmail("") },
    );
  };

  const errorCode =
    addMember.error instanceof AddMemberError ? addMember.error.code : "unknown";

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-4 rounded-lg border border-secondary-200 bg-secondary-50 p-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label
            htmlFor="add-member-email"
            className="mb-1 block text-sm font-medium text-secondary-700"
          >
            {t("teams.addMemberEmailLabel")}
          </label>
          <input
            id="add-member-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("teams.addMemberEmailPlaceholder")}
            className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
        </div>
        <div>
          <label
            htmlFor="add-member-role"
            className="mb-1 block text-sm font-medium text-secondary-700"
          >
            {t("teams.addMemberRoleLabel")}
          </label>
          <select
            id="add-member-role"
            value={role}
            onChange={(e) => setRole(e.target.value as TeamMemberRole)}
            className="rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={addMember.isPending}
          className="btn-primary inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-2 text-sm disabled:opacity-50"
        >
          {addMember.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <UserPlus className="h-4 w-4" aria-hidden="true" />
          )}
          {t("teams.addMember")}
        </button>
      </div>
      {addMember.isError && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {t(ERROR_KEYS[errorCode])}
        </p>
      )}
    </form>
  );
}
